import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Divider,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Switch,
  Popover,
  Paper,
  Grid,
  Radio,
  RadioGroup,
  CircularProgress
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  Category as CategoryIcon,
  AccountBalance as AccountIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  RepeatOne as RepeatIcon,
  History as HistoryIcon,
  DeleteOutline as DeleteOutlineIcon,
  LocalOffer as TagIcon,
  Storefront as StorefrontIcon,
  MonetizationOn as MonetizationOnIcon,
  BrightnessAuto as BrightnessAutoIcon,
  Today as TodayIcon,
  WbSunny as SunnyIcon,
  Cloud as CloudIcon,
  Opacity as RainIcon,
  AcUnit,
  Thunderstorm as StormIcon,
  Check as CheckIcon,
  FilterVintage as FilterVintageIcon,
  BeachAccess as BeachAccessIcon,
  Spa as SpaIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Flag as FlagIcon,
  ContactSupport as ContactSupportIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ThemeContext } from '../App';
import { useCalendar } from '../contexts/CalendarContext';
import WeatherThemedLayout from './WeatherThemedLayout';
import packageInfo from '../../package.json';
import { closeAllDetachableComponents } from '../utils/detachableHelpers';
import Breadcrumbs from './Breadcrumbs';
import ContactForm from './ContactForm';
import DevicesIcon from '@mui/icons-material/Devices';
import { systemApi } from '../services/api';

const expandedDrawerWidth = 210;
const collapsedDrawerWidth = 64; // Width when collapsed

// Add a new constant for z-index management
const DRAWER_Z_INDEX = 1200;
const THEME_DRAWER_Z_INDEX = 1150; // Lower than the main drawer

// Width of the theme drawer
const THEME_DRAWER_WIDTH = 400;

// Static - doesn't depend on any props/state, so it's hoisted to module
// scope instead of being rebuilt (including re-instantiating all 10 icon
// elements) on every Navigation render.
const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Transactions', icon: <ReceiptIcon />, path: '/transactions' },
  { text: 'Trending', icon: <TrendingUpIcon />, path: '/transactions/trending' },
  { text: 'Categories', icon: <CategoryIcon />, path: '/categories' },
  { text: 'Accounts', icon: <AccountIcon />, path: '/accounts' },
  { text: 'Merchants', icon: <StorefrontIcon />, path: '/merchants' },
  { text: 'Recurring', icon: <RepeatIcon />, path: '/recurring-transactions' },
  { text: 'Tags', icon: <TagIcon />, path: '/tags' },
  { text: 'Goals', icon: <FlagIcon />, path: '/goals' },
  { text: 'Reports', icon: <AnalyticsIcon />, path: '/monthly-analysis' },
];

const Navigation = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [clearRemembered, setClearRemembered] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    mode, 
    sidebarConfig, 
    updateSidebarConfig,
    weatherThemeEnabled,
    toggleWeatherTheme,
    weatherThemeMode,
    updateWeatherThemeMode,
    updateSelectedWeatherTheme,
    selectedWeatherTheme
  } = useContext(ThemeContext);
  
  // Get calendar context values
  const { toggleCalendar } = useCalendar();
  
  // State to track if sidebar is expanded or collapsed
  const [expanded, setExpanded] = useState(true);
  
  // Theme drawer state
  const [themeDrawerOpen, setThemeDrawerOpen] = useState(false);
  
  // Contact form state
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [commitHistoryOpen, setCommitHistoryOpen] = useState(false);
  const [commitHistory, setCommitHistory] = useState([]);
  const [commitHistoryLoading, setCommitHistoryLoading] = useState(false);
  const [commitHistoryError, setCommitHistoryError] = useState('');
  
  // Load the expanded state from localStorage when component mounts
  useEffect(() => {
    const savedExpanded = localStorage.getItem('sidebarExpanded');
    if (savedExpanded !== null) {
      setExpanded(savedExpanded === 'true');
    }
  }, []);
  
  // Save expanded state to localStorage when it changes
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('sidebarExpanded', expanded.toString());
    }
  }, [expanded, currentUser]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const toggleDrawerExpanded = () => {
    setExpanded(!expanded);
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    // Close detachable components before logout
    closeAllDetachableComponents();
    setLogoutDialogOpen(false);
    logout(clearRemembered);
  };

  const formatCommitDate = (isoString) => {
    if (!isoString) return '';
    try {
      const parsed = new Date(isoString);
      if (Number.isNaN(parsed.getTime())) {
        return isoString;
      }
      return parsed.toLocaleDateString();
    } catch {
      return isoString;
    }
  };

  const openCommitHistoryModal = async () => {
    setCommitHistoryOpen(true);
    setCommitHistoryLoading(true);
    setCommitHistoryError('');
    try {
      const response = await systemApi.getCommitHistory();
      setCommitHistory(response.data?.commits || []);
    } catch (error) {
      const fallbackMessage =
        error?.userMessage ||
        error?.response?.data?.error ||
        'Unable to load commit history.';
      setCommitHistory([]);
      setCommitHistoryError(fallbackMessage);
    } finally {
      setCommitHistoryLoading(false);
    }
  };

  const handleVersionEasterEggClick = () => {
    if (!currentUser) {
      return;
    }
    openCommitHistoryModal();
  };

  // Toggle hideable feature
  const toggleHideable = () => {
    updateSidebarConfig({ hideable: !sidebarConfig.hideable });
  };
  
  const closeThemeDrawer = () => {
    setThemeDrawerOpen(false);
  };
  
  // Handle theme mode change
  const handleThemeModeChange = (mode) => {
    updateWeatherThemeMode(mode);
  };
  
  // Handle specific theme selection
  const handleThemeSelect = (theme) => {
    updateSelectedWeatherTheme(theme);
  };

  const drawer = (
    <div>
      <Toolbar sx={{ 
        display: 'flex',
        justifyContent: expanded ? 'space-between' : 'center',
        px: expanded ? 2 : 1
      }}>
        {expanded ? (
          <>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              {/* Row 1: Title */}
              <Typography 
                variant="h6" 
                noWrap 
                component={RouterLink} 
                to="/dashboard"
                sx={{ 
                  textDecoration: 'none', 
                  color: 'inherit',
                  lineHeight: 1.2
                }}
              >
                Expense Tracker
              </Typography>
              {/* Row 2: Version */}
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  lineHeight: 1,
                  mt: 0.2,
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
                onClick={handleVersionEasterEggClick}
              >
                v{packageInfo.version}
              </Typography>
            </Box>
            {sidebarConfig?.hideable && (
              <IconButton onClick={toggleDrawerExpanded} size="small">
                <ChevronLeftIcon />
              </IconButton>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img 
              src="/logo.svg" 
              alt="Logo" 
              style={{ 
                width: '32px', 
                height: '32px',
                cursor: 'pointer'
              }}
              onClick={toggleDrawerExpanded}
            />
          </Box>
        )}
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item, index) => (
          <React.Fragment key={item.text}>
            <Tooltip title={!expanded ? item.text : ""} placement="right" arrow={!expanded}>
              <ListItem
                button
                component={RouterLink}
                to={item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                selected={location.pathname === item.path}
                sx={{ 
                  justifyContent: expanded ? 'flex-start' : 'center',
                  px: expanded ? 2 : 1,
                  py: 0.5,
                  minHeight: '40px'
                }}
              >
                <ListItemIcon sx={{ minWidth: expanded ? 32 : 'auto' }}>
                  {item.icon}
                </ListItemIcon>
                {expanded && <ListItemText primary={item.text} sx={{ '& .MuiListItemText-primary': { fontSize: '1rem', lineHeight: 1.2 } }} />}
              </ListItem>
            </Tooltip>
          </React.Fragment>
        ))}
      </List>
      
      {currentUser && (
        <>
          <Divider />
          <List>
            {/* Settings link */}
            <Tooltip title={!expanded ? "Settings" : ""} placement="right" arrow={!expanded}>
              <ListItem 
                button 
                component={RouterLink} 
                to="/settings"
                selected={location.pathname === '/settings'}
                sx={{ 
                  justifyContent: expanded ? 'flex-start' : 'center',
                  px: expanded ? 2 : 1,
                  py: 0.5,
                  minHeight: '40px'
                }}
              >
                <ListItemIcon sx={{ minWidth: expanded ? 32 : 'auto' }}>
                  <SettingsIcon />
                </ListItemIcon>
                {expanded && <ListItemText primary="Settings" sx={{ '& .MuiListItemText-primary': { fontSize: '1rem', lineHeight: 1.2 } }} />}
              </ListItem>
            </Tooltip>

            {/* Sessions link (below Settings) */}
            <Tooltip title={!expanded ? "Sessions" : ""} placement="right" arrow={!expanded}>
              <ListItem 
                button 
                component={RouterLink} 
                to="/sessions"
                selected={location.pathname === '/sessions'}
                sx={{ 
                  justifyContent: expanded ? 'flex-start' : 'center',
                  px: expanded ? 2 : 1,
                  py: 0.5,
                  minHeight: '40px'
                }}
              >
                <ListItemIcon sx={{ minWidth: expanded ? 32 : 'auto' }}>
                  <DevicesIcon />
                </ListItemIcon>
                {expanded && <ListItemText primary="Sessions" sx={{ '& .MuiListItemText-primary': { fontSize: '1rem', lineHeight: 1.2 } }} />}
              </ListItem>
            </Tooltip>
            
            {/* Support link */}
            <Tooltip title={!expanded ? "Support" : ""} placement="right" arrow={!expanded}>
              <ListItem
                button
                onClick={() => setContactFormOpen(true)}
                sx={{ 
                  justifyContent: expanded ? 'flex-start' : 'center',
                  px: expanded ? 2 : 1,
                  py: 0.5,
                  minHeight: '40px'
                }}
              >
                <ListItemIcon sx={{ minWidth: expanded ? 32 : 'auto' }}>
                  <ContactSupportIcon />
                </ListItemIcon>
                {expanded && <ListItemText primary="Support" sx={{ '& .MuiListItemText-primary': { fontSize: '1rem', lineHeight: 1.2 } }} />}
              </ListItem>
            </Tooltip>

            {/* Logout option */}
            <Tooltip title={!expanded ? "Logout" : ""} placement="right" arrow={!expanded}>
              <ListItem 
                button 
                onClick={handleLogoutClick}
                sx={{ 
                  justifyContent: expanded ? 'flex-start' : 'center',
                  px: expanded ? 2 : 1,
                  py: 0.5,
                  minHeight: '40px'
                }}
              >
                <ListItemIcon sx={{ minWidth: expanded ? 32 : 'auto' }}>
                  <LogoutIcon />
                </ListItemIcon>
                {expanded && <ListItemText primary="Logout" sx={{ '& .MuiListItemText-primary': { fontSize: '1rem', lineHeight: 1.2 } }} />}
              </ListItem>
            </Tooltip>
          </List>
          
          {/* Admin Section intentionally hidden to keep admin routes accessible without sidebar links */}
        </>
      )}
    </div>
  );

  // Helper function to render weather theme card
  const renderWeatherThemeCard = (themeType, label, icon) => {
    const isSelected = selectedWeatherTheme === themeType;
    const isDark = mode === 'dark';
    
    // Get background color based on theme type
    const getThemeBackground = () => {
      switch(themeType) {
        case 'clear':
          return isDark
            ? 'linear-gradient(to right, #e65100, #ff9800)' // Dark orange to orange
            : 'linear-gradient(to right, #ff9800, #ffc107)'; // Orange to amber
        case 'clouds':
          return isDark
            ? 'linear-gradient(to right, #263238, #455a64)' // Dark gray gradient
            : 'linear-gradient(to right, #78909c, #b0bec5)'; // Light gray gradient
        case 'rain':
        case 'drizzle':
          return isDark
            ? 'linear-gradient(to right, #00695c, #00897b)' // Dark teal gradient
            : 'linear-gradient(to right, #00897b, #26a69a)'; // Teal gradient
        case 'snow':
          return isDark
            ? 'linear-gradient(to right, #90a4ae, #cfd8dc)' // Bluish gray gradient
            : 'linear-gradient(to right, #cfd8dc, #eceff1)'; // Lighter blue-gray
        case 'thunderstorm':
          return isDark
            ? 'linear-gradient(to right, #4a148c, #6a1b9a)' // Deep purple gradient
            : 'linear-gradient(to right, #7b1fa2, #9c27b0)'; // Purple gradient
        case 'spring':
          return isDark
            ? 'linear-gradient(to right, #43a047, #66bb6a)' // Dark green to green gradient
            : 'linear-gradient(to right, #66bb6a, #81c784)'; // Green gradient for spring
        case 'summer':
          return isDark
            ? 'linear-gradient(to right, #0097a7, #26c6da)' // Dark cyan to cyan gradient
            : 'linear-gradient(to right, #00bcd4, #4dd0e1)'; // Cyan gradient for summer
        case 'autumn':
          return isDark
            ? 'linear-gradient(to right, #e64a19, #ff7043)' // Dark orange to orange gradient
            : 'linear-gradient(to right, #ff5722, #ff8a65)'; // Orange gradient for autumn
        case 'winter':
          return isDark
            ? 'linear-gradient(to right, #1976d2, #42a5f5)' // Dark blue to blue gradient
            : 'linear-gradient(to right, #2196f3, #64b5f6)'; // Blue gradient for winter
        default:
          return isDark ? '#1e1e1e' : '#ffffff';
      }
    };

    return (
      <Paper
        elevation={isSelected ? 8 : 2}
        sx={{
          height: 80,
          borderRadius: 2,
          overflow: 'hidden',
          cursor: 'pointer',
          border: isSelected ? '2px solid' : '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          position: 'relative',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4
          }
        }}
        onClick={() => handleThemeSelect(themeType)}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            background: getThemeBackground(),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {React.cloneElement(icon, { 
            sx: { 
              fontSize: '2rem',
              mb: 0.5,
              color: themeType === 'clear' ? '#FFD700' : 
                     themeType === 'clouds' ? '#E0E0E0' :
                     themeType === 'rain' ? '#E0F2F1' :
                     themeType === 'snow' ? '#FFFFFF' : 
                     themeType === 'thunderstorm' ? '#E1BEE7' : '#FFFFFF'
            } 
          })}
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#fff',
              fontWeight: 'medium',
              textShadow: '0px 0px 4px rgba(0,0,0,0.5)'
            }}
          >
            {label}
          </Typography>
        </Box>
        {isSelected && (
          <CheckIcon 
            sx={{ 
              position: 'absolute',
              top: 5,
              right: 5,
              color: 'primary.main',
              background: 'rgba(255,255,255,0.7)',
              borderRadius: '50%',
              p: 0.2,
              fontSize: '1rem'
            }} 
          />
        )}
      </Paper>
    );
  };

  // Theme drawer content
  const themeDrawerContent = (
    <Box sx={{ width: THEME_DRAWER_WIDTH, p: 2, overflow: 'auto' }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 2 
      }}>
        <Typography variant="h6">Theme Settings</Typography>
        <IconButton onClick={closeThemeDrawer} size="small">
          <ChevronRightIcon />
        </IconButton>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      <FormControlLabel
        control={
          <Switch 
            checked={weatherThemeEnabled}
            onChange={toggleWeatherTheme}
            color="primary"
          />
        }
        label="Weather Themes"
        sx={{ mb: 2, display: 'block' }}
      />
      
      {weatherThemeEnabled && (
        <>
          <Typography variant="subtitle2" gutterBottom>Theme Mode</Typography>
          <RadioGroup 
            value={weatherThemeMode} 
            onChange={(e) => handleThemeModeChange(e.target.value)}
          >
            <FormControlLabel value="manual" control={<Radio size="small" />} label="Manual Selection" />
            <FormControlLabel value="cycle_weather" control={<Radio size="small" />} label="Daily Cycle - Weather" />
            <FormControlLabel value="cycle_seasons" control={<Radio size="small" />} label="Daily Cycle - Seasons" />
          </RadioGroup>
          
          {weatherThemeMode === 'manual' && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Weather Themes</Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  {renderWeatherThemeCard('clear', 'Clear', <SunnyIcon />)}
                </Grid>
                <Grid item xs={4}>
                  {renderWeatherThemeCard('clouds', 'Cloudy', <CloudIcon />)}
                </Grid>
                <Grid item xs={4}>
                  {renderWeatherThemeCard('rain', 'Rainy', <RainIcon />)}
                </Grid>
                <Grid item xs={4}>
                  {renderWeatherThemeCard('snow', 'Snowy', <AcUnit />)}
                </Grid>
                <Grid item xs={4}>
                  {renderWeatherThemeCard('thunderstorm', 'Storm', <StormIcon />)}
                </Grid>
                
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant="subtitle2">Seasonal Themes</Typography>
                </Grid>
                
                <Grid item xs={3}>
                  {renderWeatherThemeCard('spring', 'Spring', <SpaIcon />)}
                </Grid>
                <Grid item xs={3}>
                  {renderWeatherThemeCard('summer', 'Summer', <BeachAccessIcon />)}
                </Grid>
                <Grid item xs={3}>
                  {renderWeatherThemeCard('autumn', 'Autumn', <FilterVintageIcon />)}
                </Grid>
                <Grid item xs={3}>
                  {renderWeatherThemeCard('winter', 'Winter', <AcUnit />)}
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}
    </Box>
  );

  // Calculate current drawer width based on expanded state
  const canCollapse = sidebarConfig?.hideable !== false;
  const currentDrawerWidth = canCollapse && !expanded ? collapsedDrawerWidth : expandedDrawerWidth;
  const commitCount = commitHistory?.length || 0;
  const latestCommitTimestamp =
    commitCount > 0 && commitHistory[0]?.date
      ? formatCommitDate(commitHistory[0].date)
      : '';

  // `children` (the actual routed page) is a stable reference across this
  // component's own re-renders - it comes from Navigation's parent, which
  // doesn't re-render just because Navigation's own local state (sidebar
  // toggle, dialogs, ...) changed. But composing it with <Breadcrumbs />
  // inline, as before, created a brand new element/array on every
  // Navigation render regardless, which defeated WeatherThemedLayout's
  // React.memo below by giving it a "changed" children prop every time.
  const weatherLayoutChildren = useMemo(() => (
    <>
      <Breadcrumbs />
      {children}
    </>
  ), [children]);

  return (
    <Box sx={{ display: 'flex' }}>
      <Box
        component="nav"
        sx={{ 
          width: { sm: currentDrawerWidth }, 
          flexShrink: { sm: 0 },
          position: 'fixed',
          zIndex: DRAWER_Z_INDEX,
          height: '100%',
          pointerEvents: expanded ? 'auto' : 'none',
        }}
      >
        {currentUser && (
          <>
            {/* Mobile drawer */}
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile
              }}
              sx={{
                display: { xs: 'block', sm: 'none' },
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: currentDrawerWidth,
                  transition: 'width 0.2s ease',
                  zIndex: DRAWER_Z_INDEX,
                  pointerEvents: 'auto',
                },
              }}
            >
              {drawer}
            </Drawer>

            {/* Desktop drawer */}
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', sm: 'block' },
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: currentDrawerWidth,
                  overflowX: 'hidden',
                  transition: 'width 0.2s ease',
                  zIndex: DRAWER_Z_INDEX,
                  pointerEvents: 'auto',
                },
              }}
              open
            >
              {drawer}
            </Drawer>
            
            {/* Theme settings drawer - slides from right */}
            <Drawer
              variant="temporary"
              anchor="right"
              open={themeDrawerOpen}
              onClose={closeThemeDrawer}
              sx={{
                '& .MuiDrawer-paper': { 
                  width: THEME_DRAWER_WIDTH,
                  zIndex: THEME_DRAWER_Z_INDEX,
                  borderTopLeftRadius: 8,
                  borderBottomLeftRadius: 8,
                  boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.1)',
                },
              }}
              ModalProps={{
                keepMounted: true,
              }}
            >
              {themeDrawerContent}
            </Drawer>
          </>
        )}
      </Box>
      
      {/* Logout confirmation dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
      >
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Do you want to log out?
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={clearRemembered}
                onChange={(e) => setClearRemembered(e.target.checked)}
                name="clearRemembered"
              />
            }
            label="Clear remembered login"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLogoutConfirm} color="primary">Logout</Button>
        </DialogActions>
      </Dialog>
      
      {/* Contact form dialog */}
      <ContactForm 
        open={contactFormOpen}
        onClose={() => setContactFormOpen(false)}
      />

      {/* Commit history dialog */}
      <Dialog
        open={commitHistoryOpen}
        onClose={() => setCommitHistoryOpen(false)}
        maxWidth="sm"
        fullWidth={false}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }),
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 1.5,
            px: 3,
          }}
        >
          <HistoryIcon fontSize="small" />
          {`v${packageInfo.version} • Recent Changes (${commitCount})${latestCommitTimestamp ? ` • ${latestCommitTimestamp}` : ''}`}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[50]
                : theme.palette.background.default,
          }}
        >
          {commitHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : commitHistoryError ? (
            <Typography color="error">{commitHistoryError}</Typography>
          ) : commitHistory.length === 0 ? (
            <Typography color="text.secondary">
              No commit history found.
            </Typography>
          ) : (
            <List disablePadding>
              {commitHistory.map((commit, index) => (
                <ListItem
                  key={commit.hash}
                  alignItems="flex-start"
                  sx={{ py: 0.75 }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ width: 24, flexShrink: 0 }}
                  >
                    {index + 1}.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {commit.message}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommitHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { sm: 0 },
          width: '100%',
          paddingLeft: { 
            xs: 0, 
            sm: expanded ? `${expandedDrawerWidth}px` : `${collapsedDrawerWidth}px` // Adjust padding based on drawer state
          },
          transition: 'padding-left 0.2s ease', // Add transition for smooth adjustment
          display: 'flex',
          justifyContent: 'flex-start' // Always left-align content
        }}
      >
        {/* Main content container */}
        <Container
          maxWidth="lg"
          sx={{
            py: 2,
            minHeight: '100vh',
            boxSizing: 'border-box',
            maxWidth: theme => ({
              xs: '95%', // Use 95% of screen on smaller devices
              lg: '1200px', // Cap absolute width for large screens
              xl: '1200px', // Keep the same cap for extra large screens
            }),
            position: 'relative' // Keep position relative to container
          }}
        >
          <WeatherThemedLayout>
            {weatherLayoutChildren}
          </WeatherThemedLayout>
        </Container>
      </Box>
    </Box>
  );
};

export default Navigation;
